/**
 * Participants, what they entered, and whether they turned up.
 *
 * The split that carries D14: `registrations` is participant × event and holds identity;
 * `registration_entries` is registration × competition × session and records what someone dances
 * and when. A dancer in three categories across two days is six entries against one
 * registration, not six registrations — which is what lets a daily honorarium and a
 * once-per-weekend placement prize both attach cleanly.
 */

import { sql } from "drizzle-orm";
import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { archivable, audited, flag, id, instant, json, ref } from "./columns.js";

/**
 * A person who takes part. Persists across events within a community, so a returning dancer is
 * found by search rather than retyped — which is what makes the registration desk's hundredth
 * registration of the morning a confirmation rather than an entry (ux.md §3.3).
 *
 * `email` is optional throughout. Every contact on the pilot community's poster is a phone
 * number, and registrants are as young as four (D21).
 */
export const PARTICIPANT_STATUSES = ["active", "inactive", "merged"] as const;

export const participants = sqliteTable(
  "participants",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    firstName: text("first_name").notNull(),
    lastName: text("last_name").notNull(),
    /** What they go by. Null falls back to `firstName`. */
    preferredName: text("preferred_name"),
    /** What the leaderboard prints, when it differs from the legal name. Null falls back. */
    publicDisplayName: text("public_display_name"),

    /**
     * Home community, as free text and — when it is one the platform knows — as a reference.
     *
     * Both, not one. `affiliationCommunityId` points at the `communities` directory, which is what
     * makes "every dancer from Curve Lake" a join rather than a spelling contest across
     * "Curve Lake", "Curve Lake FN" and "curve lake first nation". `communityAffiliation` stays
     * because the list is not the world: a dancer travelling from Manitoba, or one whose nation
     * simply is not in it yet, must still be registerable at a desk with a queue behind them.
     * The reference is the fast path, never the gate.
     */
    affiliationCommunityId: ref("affiliation_community_id"),
    communityAffiliation: text("community_affiliation"),
    phone: text("phone"),
    email: text("email"),
    gender: text("gender"),
    address: text("address"),
    city: text("city"),
    region: text("region"),
    postalCode: text("postal_code"),
    country: text("country"),
    guardianName: text("guardian_name"),
    guardianPhone: text("guardian_phone"),
    /** Distinct from the guardian: an adult dancer's emergency contact is not a guardian. */
    emergencyContactName: text("emergency_contact_name"),
    emergencyContactPhone: text("emergency_contact_phone"),
    notes: text("notes"),

    /**
     * `merged` marks a duplicate whose records were repointed at `mergedIntoId`. The row stays,
     * because prize history hanging off a deleted person is exactly the audit gap a merge exists
     * to prevent — search simply stops returning it.
     */
    status: text("status", { enum: PARTICIPANT_STATUSES }).notNull().default("active"),
    mergedIntoId: ref("merged_into_id"),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    index("participants_community_name_idx").on(table.communityId, table.lastName, table.firstName),
  ],
);

export const REGISTRATION_CHANNELS = ["self", "staff"] as const;
export const REGISTRATION_STATUSES = ["active", "cancelled"] as const;

export const registrations = sqliteTable(
  "registrations",
  {
    id: id(),
    eventId: ref("event_id").notNull(),
    communityId: ref("community_id").notNull(),
    participantId: ref("participant_id").notNull(),
    formSubmissionId: ref("form_submission_id"),

    /**
     * The registration's own id, human-readable: `REG-0001`, sequential per event. Every
     * registration has one and it never changes, so it is the handle a desk searches by, a slip
     * prints, and an export, payout sheet or check-in card carries to say which registration a row
     * is. Distinct from `dancerNumber` — this identifies the registration, not the dancer in the
     * arena — which is why it is mandatory where the dancer number is not.
     */
    registrationNumber: text("registration_number").notNull(),

    /** The number worn in the arena. Judges score by number, which is what makes blind judging
     *  possible at all. */
    dancerNumber: text("dancer_number"),

    /**
     * How old they are at this event, in completed years. What resolves an age band (D18).
     *
     * On the registration and not on the person, because an age is only true of a date. A pow wow
     * runs once a year: a number kept on the participant would be silently wrong for eleven of
     * every twelve months, and re-asking it next year would rewrite what last year's tabulation and
     * analytics were computed from. Here it is a fact about one registration, and it stays true.
     *
     * Null is ordinary and not an error — an adult competition has no reason to ask, and a desk
     * with a queue behind it should not be blocked on a number nobody uses.
     */
    age: integer("age"),

    channel: text("channel", { enum: REGISTRATION_CHANNELS }).notNull(),

    /**
     * Which desk issued the numbers, when the desk was told (D86).
     *
     * A device fact, not a person's — one registrar works whichever table is short-handed, so
     * `registeredBy` cannot answer it. Null is the ordinary case and means a single desk that was
     * never given a station, whose numbers are allocated across the whole sequence as they always
     * were. Kept because it is the only way to tell afterwards which desk a number came from, which
     * is what a reconciliation between two desks' work needs.
     */
    station: integer("station"),

    /**
     * The machine that issued the numbers, when it was a numbered desk.
     *
     * Set only alongside `station`, and only so that two machines accidentally set to the *same*
     * desk can be noticed. Nothing else reads it and nothing depends on it: the bands already make
     * that mistake harmless to the numbers — both machines draw from the same band, the unique index
     * refuses a duplicate and the retry hands out the next one — but harmless is not the same as
     * visible, and a desk that thinks it is the only Desk 2 should be told it is not.
     *
     * The device's own opaque id, the same one a judge's access code binds to. It says "a browser",
     * and nothing about a person.
     */
    stationDeviceId: text("station_device_id"),

    registeredBy: ref("registered_by"),
    registeredAt: instant("registered_at").notNull(),
    status: text("status", { enum: REGISTRATION_STATUSES }).notNull().default("active"),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    uniqueIndex("registrations_event_participant_unique").on(table.eventId, table.participantId),
    uniqueIndex("registrations_number_unique").on(table.eventId, table.registrationNumber),
    uniqueIndex("registrations_dancer_number_unique").on(table.eventId, table.dancerNumber),
    index("registrations_event_idx").on(table.eventId, table.registeredAt),
  ],
);

export const ENTRY_STATUSES = ["entered", "withdrawn", "no_show"] as const;

/**
 * What someone is entered in, and when.
 *
 * Null `sessionId` means the entry stands for every session the competition runs.
 *
 * `overrideWindowId` and `overrideReason` record a registration taken after the window closed.
 * That path exists because a dancer arriving at 12:35pm is a person standing at a table, not an
 * error state — refusing it would push the real workflow onto paper and lose the record
 * entirely (D14).
 */
export const registrationEntries = sqliteTable(
  "registration_entries",
  {
    id: id(),
    registrationId: ref("registration_id").notNull(),
    communityId: ref("community_id").notNull(),
    competitionId: ref("competition_id").notNull(),
    categoryId: ref("category_id"),
    sessionId: ref("session_id"),
    status: text("status", { enum: ENTRY_STATUSES }).notNull().default("entered"),

    /** Present when this entry belongs to a team — the drum group, the canoe crew (D33). */
    teamId: ref("team_id"),

    overrideWindowId: ref("override_window_id"),
    overrideReason: text("override_reason"),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    uniqueIndex("registration_entries_unique").on(
      table.registrationId,
      table.competitionId,
      table.categoryId,
      table.sessionId,
    ),
    index("registration_entries_category_idx").on(table.categoryId, table.status),
    index("registration_entries_session_idx").on(table.sessionId),
  ],
);

export const TEAM_STATUSES = ["forming", "registered", "withdrawn"] as const;

/**
 * A team that competes as one entrant — a drum group, a canoe crew, a horseshoe pair (D33).
 *
 * The team is scored and placed; the members are people, so payouts can still be recorded
 * against a person and `distributionNote` records how a shared prize was actually split, which
 * is the treasurer's question, not the scorekeeper's.
 */
export const teams = sqliteTable(
  "teams",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    eventId: ref("event_id").notNull(),
    competitionId: ref("competition_id").notNull(),
    categoryId: ref("category_id"),
    name: text("name").notNull(),
    /** The number worn or called, like a dancer number. Unique per event where present. */
    teamNumber: text("team_number"),
    captainParticipantId: ref("captain_participant_id"),
    contactEmail: text("contact_email"),
    contactPhone: text("contact_phone"),
    /** Same pair as a participant's, for the same reason — a visiting drum group is normal. */
    affiliationCommunityId: ref("affiliation_community_id"),
    communityAffiliation: text("community_affiliation"),
    status: text("status", { enum: TEAM_STATUSES }).notNull().default("forming"),
    /** How a shared prize was distributed — free text, because communities decide this. */
    distributionNote: text("distribution_note"),
    notes: text("notes"),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    uniqueIndex("teams_event_number_unique").on(table.eventId, table.teamNumber),
    index("teams_competition_idx").on(table.competitionId),
  ],
);

export const teamMembers = sqliteTable(
  "team_members",
  {
    id: id(),
    teamId: ref("team_id").notNull(),
    communityId: ref("community_id").notNull(),
    participantId: ref("participant_id").notNull(),
    /** Free text — "captain", "second drum", whatever the community calls it. */
    roleLabel: text("role_label"),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    uniqueIndex("team_members_unique").on(table.teamId, table.participantId),
    index("team_members_participant_idx").on(table.participantId),
  ],
);

export const CONSENT_TYPES = ["photo", "media", "liability", "data", "guardian"] as const;

export const consents = sqliteTable(
  "consents",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    participantId: ref("participant_id").notNull(),
    registrationId: ref("registration_id"),
    type: text("type", { enum: CONSENT_TYPES }).notNull(),
    granted: flag("granted").notNull(),
    grantedBy: text("granted_by"),
    grantedAt: instant("granted_at").notNull(),
    /** The wording actually agreed to, so a later edit to the consent text cannot rewrite history. */
    textVersion: text("text_version").notNull(),
    ...audited(),
  },
  (table) => [index("consents_participant_idx").on(table.participantId, table.type)],
);

export const documents = sqliteTable(
  "documents",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    participantId: ref("participant_id").notNull(),
    type: text("type").notNull(),
    attachmentId: ref("attachment_id").notNull(),
    verifiedBy: ref("verified_by"),
    verifiedAt: instant("verified_at"),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("documents_participant_idx").on(table.participantId)],
);

/**
 * What a participant token opens (D66).
 *
 * `manage` is the key to `/manage/{token}`: it edits entries, adds them and withdraws from the
 * event. `check_in` only says *who this is* — it is the one printed as a QR on a confirmation and
 * held up at a gate, where it is photographed over shoulders by a queue of strangers all day. The
 * two are separated so that a code read off someone's phone buys the ability to mark them present
 * and nothing else.
 */
export const PARTICIPANT_TOKEN_PURPOSES = ["manage", "check_in"] as const;

/**
 * The key to `/manage/{token}`, and the code on the check-in slip.
 *
 * Reaches people by a printed QR slip handed over at the registration table, which works with no
 * signal, no inbox and no literacy assumption (D21). Only the hash is stored — a leaked database
 * must not be a set of working links.
 */
export const participantTokens = sqliteTable(
  "participant_tokens",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    participantId: ref("participant_id").notNull(),
    registrationId: ref("registration_id"),
    tokenHash: text("token_hash").notNull(),
    /** Defaults to `manage`, which is what every token minted before the split was. */
    purpose: text("purpose", { enum: PARTICIPANT_TOKEN_PURPOSES }).notNull().default("manage"),
    expiresAt: instant("expires_at"),
    revokedAt: instant("revoked_at"),
    lastUsedAt: instant("last_used_at"),
    ...audited(),
  },
  (table) => [
    uniqueIndex("participant_tokens_hash_unique").on(table.tokenHash),
    index("participant_tokens_participant_idx").on(table.participantId),
    // The lookup behind "which code do I print on this confirmation" — one registration, one
    // purpose, rather than a scan of every token the participant has ever held.
    index("participant_tokens_registration_idx").on(table.registrationId, table.purpose),
  ],
);

export const CHECK_IN_METHODS = ["qr", "manual", "bulk"] as const;

/**
 * Check-in is the ground truth for participation payouts, so it is reversible and audited rather
 * than a fire-and-forget tap — an accidental check-in is money (D16). Registering and not
 * dancing earns nothing.
 *
 * The id is a client-generated ULID because check-in happens offline at a gate, and the same
 * scan retried after a dropped connection must not produce a second row.
 *
 * **Two kinds of subject, one act** (D64). Exactly one of `registrationEntryId` and
 * `roleAssignmentId` is set: the first is a dancer arriving for a session, the second an elder who
 * came on the Sunday or an MC working one session of four. Paying a role holder for a day they were
 * not there is the identical error to paying a dancer who did not dance, and check-in is the
 * identical answer — so a second `role_check_ins` table was rejected. It would have given the gate
 * two tables to write, the expected/checked-in counter two sources to add up, and a reversal two
 * code paths to be correct in.
 */
export const checkIns = sqliteTable(
  "check_ins",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    eventId: ref("event_id").notNull(),
    sessionId: ref("session_id").notNull(),
    /** Set for a competitor. Null when this check-in is a role holder's. */
    registrationEntryId: ref("registration_entry_id"),
    /** Set for a role holder (D62). Null when this check-in is a competitor's. */
    roleAssignmentId: ref("role_assignment_id"),
    checkedInAt: instant("checked_in_at").notNull(),
    checkedInBy: ref("checked_in_by"),
    method: text("method", { enum: CHECK_IN_METHODS }).notNull(),
    deviceId: text("device_id"),
    reversedAt: instant("reversed_at"),
    reversedBy: ref("reversed_by"),
    reversedReason: text("reversed_reason"),
    ...audited(),
  },
  (table) => [
    /**
     * One *live* check-in per subject per session, enforced here rather than only in code.
     *
     * A duplicate scan is recognised and reported as "Already checked in at 12:42pm" rather than
     * silently creating a second record (ux.md §3.4) — the repository reads before it writes, and
     * says so. But two gate devices scanning the same person in the same second both read nothing
     * and both write, and the second row is money: a per-session participation rule would pay the
     * honorarium twice. The read-before-write stays, because it is what produces the friendly
     * answer; this is what makes the friendly answer true.
     *
     * Partial on `reversed_at IS NULL`, because a reversal has to leave the person able to check
     * in again — an accidental undo at the gate is corrected by scanning them a second time.
     */
    uniqueIndex("check_ins_entry_session_live_unique")
      .on(table.registrationEntryId, table.sessionId)
      .where(sql`reversed_at IS NULL AND registration_entry_id IS NOT NULL`),
    uniqueIndex("check_ins_role_session_live_unique")
      .on(table.roleAssignmentId, table.sessionId)
      .where(sql`reversed_at IS NULL AND role_assignment_id IS NOT NULL`),
    index("check_ins_entry_session_idx").on(table.registrationEntryId, table.sessionId),
    index("check_ins_role_session_idx").on(table.roleAssignmentId, table.sessionId),
    index("check_ins_session_idx").on(table.sessionId, table.checkedInAt),
  ],
);

/**
 * Denormalised counters for the check-in screen's live "checked in / expected" line. Kept as a
 * table rather than a query because that screen refreshes constantly on a phone at a gate, and a
 * count over every entry in the session is not what should be running there.
 */
export const sessionCounters = sqliteTable(
  "session_counters",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    sessionId: ref("session_id").notNull(),
    competitionId: ref("competition_id"),
    expectedCount: integer("expected_count").notNull().default(0),
    checkedInCount: integer("checked_in_count").notNull().default(0),
    recomputedAt: instant("recomputed_at").notNull(),
    detailJson: json("detail_json"),
    ...audited(),
  },
  (table) => [uniqueIndex("session_counters_unique").on(table.sessionId, table.competitionId)],
);
